import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EmptyState } from '@pazportal/ui';
import {
  useCustomerUptimeTrend,
  useCustomerResolutionTrend,
  useCustomerAssetRefresh,
} from '../hooks/useCustomerTrends';
import { useUnifiSites } from '../hooks/useUnifiSites';
import { useAccount } from '../contexts/AccountContext';
import { useLocations } from '../hooks/useLocations';
import { SkeletonCard } from '../components/Skeleton';
import { AccountSelect } from '../components/AccountSelect';
import { barFraction, computeBarDomain, labelPrecision } from '../lib/chartScale';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';

/**
 * Customer-facing trend views: monthly network availability, ticket
 * resolution time and asset refresh status. The RPCs are per account, so in
 * portfolio mode the page charts one chosen account at a time.
 */
export function TrendsPage() {
  const { t, locale } = useI18n();
  const { id: locationId } = useParams<{ id?: string }>();
  const { data: locationsData } = useLocations();
  const site = locationsData?.locations.find((location) => location.id === locationId);
  const { accounts, activeAccount, isPortfolio, ready } = useAccount();
  const [chosen, setChosen] = useState<string | null>(null);
  // Portfolio default: the first linked account that actually has network
  // telemetry, so a multi-property operator does not land on an empty chart.
  const { data: unifiSites } = useUnifiSites();
  const defaultPortfolioAccount = useMemo(() => {
    const withTelemetry = new Set((unifiSites ?? []).map((s) => s.customer_id));
    return accounts.find((a) => withTelemetry.has(a.customer_id))?.customer_id ?? accounts[0]?.customer_id ?? null;
  }, [accounts, unifiSites]);

  const customerId = locationId
    ? site?.customer_id ?? null
    : isPortfolio
      ? chosen && accounts.some((a) => a.customer_id === chosen)
        ? chosen
        : defaultPortfolioAccount
      : activeAccount?.customer_id ?? null;

  const uptime = useCustomerUptimeTrend(customerId, 6, locationId);
  const resolution = useCustomerResolutionTrend(customerId, 6, locationId);
  const refresh = useCustomerAssetRefresh(customerId, locationId);

  const isLoading =
    !ready || (!!customerId && (uptime.isLoading || resolution.isLoading || refresh.isLoading));
  const errors = [uptime.error, resolution.error, refresh.error].filter(
    (e): e is Error => e instanceof Error,
  );

  const monthLabel = (iso: string) => {
    const [year, month] = iso.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString(locale, {
      month: 'short',
      year: '2-digit',
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">{t('trends.title')}</h1>
          <p className="mt-0.5 text-sm text-text-muted">{t('trends.subtitle')}</p>
        </div>
        {isPortfolio && !locationId && (
          <AccountSelect value={customerId} onChange={setChosen} label={t('trends.choose_account')} />
        )}
      </div>

      {errors.length > 0 && (
        <div role="alert" className="rounded-lg border border-notification bg-notification-soft p-4 text-sm text-notification">
          {t('trends.error')} {errors[0].message}
        </div>
      )}

      {ready && accounts.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState icon={<TrendIcon />} title={t('account.none')} subtitle={t('account.none_hint')} />
        </div>
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
          <UptimeCard data={uptime.data ?? []} monthLabel={monthLabel} />
          <ResolutionCard data={resolution.data ?? []} monthLabel={monthLabel} />
          <RefreshCard data={refresh.data ?? []} />
        </div>
      )}
    </div>
  );
}

function UptimeCard({
  data,
  monthLabel,
}: {
  data: { month_start: string; uptime_pct: number }[];
  monthLabel: (iso: string) => string;
}) {
  const { t } = useI18n();
  if (data.length === 0) {
    return (
      <Card title={t('trends.uptime')} subtitle={t('trends.uptime_empty')}>
        <p className="text-xs text-text-faint">{t('trends.uptime_empty_hint')}</p>
      </Card>
    );
  }
  return (
    <Card title={t('trends.uptime')} subtitle={t('trends.uptime_sub')}>
      <SparkBars
        points={data.map((d) => ({ label: monthLabel(d.month_start), value: Number(d.uptime_pct) }))}
        cap={100}
        unit="%"
        colorFor={(v) => (v >= 99 ? 'bg-success' : v >= 95 ? 'bg-warning' : 'bg-notification')}
      />
    </Card>
  );
}

function ResolutionCard({
  data,
  monthLabel,
}: {
  data: { month_start: string; resolved_count: number; avg_resolution_hours: number }[];
  monthLabel: (iso: string) => string;
}) {
  const { t } = useI18n();
  const hasHistory = data.some((d) => d.resolved_count > 0);
  if (data.length === 0 || !hasHistory) {
    return (
      <Card title={t('trends.resolution')} subtitle={t('trends.resolution_empty')}>
        <p className="text-xs text-text-faint">{t('trends.resolution_empty_hint')}</p>
      </Card>
    );
  }
  return (
    <Card title={t('trends.resolution')} subtitle={t('trends.resolution_sub')}>
      <SparkBars
        points={data.map((d) => ({
          label: monthLabel(d.month_start),
          value: Number(d.avg_resolution_hours) || 0,
          sub: d.resolved_count > 0 ? t('trends.closed', { n: d.resolved_count }) : t('common.none'),
        }))}
        unit="h"
        colorFor={(v) => (v <= 8 ? 'bg-success' : v <= 24 ? 'bg-warning' : 'bg-notification')}
      />
    </Card>
  );
}

const BUCKET_COLOR: Record<string, string> = {
  expired: 'text-notification',
  expiring_90d: 'text-warning',
  expiring_1y: 'text-info',
  healthy: 'text-success',
  no_warranty_data: 'text-text-faint',
};

function RefreshCard({
  data,
}: {
  data: { bucket: string; asset_count: number; example_solution_types: string[] }[];
}) {
  const { t } = useI18n();
  if (data.length === 0) {
    return (
      <Card title={t('trends.refresh')} subtitle={t('trends.refresh_empty')}>
        <p className="text-xs text-text-faint">{t('trends.refresh_empty_hint')}</p>
      </Card>
    );
  }
  return (
    <Card title={t('trends.refresh')} subtitle={t('trends.refresh_sub')}>
      <ul className="divide-y divide-divider">
        {data.map((r) => {
          const key = `trends.bucket.${r.bucket}` as MessageKey;
          return (
            <li key={r.bucket} className="flex items-center justify-between py-2.5">
              <div>
                <p className={`text-sm ${BUCKET_COLOR[r.bucket] ?? 'text-text'}`}>{t(key)}</p>
                {r.example_solution_types.length > 0 && (
                  <p className="truncate text-xs text-text-faint">
                    {r.example_solution_types.slice(0, 4).join(', ')}
                  </p>
                )}
              </div>
              <span className="text-lg font-semibold text-text">{r.asset_count}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-label={title}>
      <h3 className="text-sm font-medium text-text-muted">{title}</h3>
      {subtitle && <p className="mt-0.5 text-xs text-text-faint">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/**
 * A small column chart for a handful of monthly points.
 *
 * Three things it deliberately does, each fixing how this read before:
 *
 *  • **The domain is padded, and says so.** Availability sits at 97–98%, and
 *    against a fixed 0–100 axis every bar came out within a pixel of every
 *    other — two identical blocks carrying no information. A clipped baseline
 *    separates them, so the baseline value is printed on the axis. Clipping an
 *    axis is legitimate; not saying you clipped it is deceptive.
 *  • **The bars are capped at 24px.** They used to be `w-full`, so with two
 *    points each "bar" was a 120px slab. Thin marks, and the leftover of the
 *    band is air.
 *  • **Labels are selective.** A number on every point goes unread. The latest
 *    point and the extreme are labelled; the rest are reachable by hover and by
 *    the table-view list every reader gets below.
 */
function SparkBars({
  points,
  cap,
  unit,
  colorFor,
}: {
  points: { label: string; value: number; sub?: string }[];
  /** Upper bound the domain may not exceed, e.g. 100 for a percentage. */
  cap?: number;
  unit: string;
  colorFor: (v: number) => string;
}) {
  const values = points.map((p) => p.value);
  const domain = computeBarDomain(values, { cap });
  // Enough decimals that the labels do not announce a change the data does not
  // contain: 97.49 and 97.53 must not read as "97%" and "98%".
  const decimals = labelPrecision(values);

  // Which points earn a printed number: the most recent, and the extreme if it
  // is not already the most recent.
  const latestIndex = points.length - 1;
  let extremeIndex = 0;
  for (let i = 1; i < points.length; i += 1) {
    if (Math.abs(points[i].value - points[latestIndex].value) >
        Math.abs(points[extremeIndex].value - points[latestIndex].value)) {
      extremeIndex = i;
    }
  }
  const labelled = new Set([latestIndex, extremeIndex]);

  return (
    <>
      <div className="flex h-32 items-stretch gap-2" aria-hidden="true">
        {points.map((point, index) => {
          const fraction = barFraction(point.value, domain);
          // A floor so a value sitting on the baseline is still a visible mark
          // rather than nothing at all, but small enough not to read as height.
          const heightPct = Math.max(2, fraction * 100);
          const showValue = labelled.has(index);
          return (
            <div key={point.label} className="flex min-h-0 flex-1 flex-col items-center gap-1">
              <span className="h-4 shrink-0 text-[10px] tabular-nums text-text-muted">
                {showValue ? `${point.value.toFixed(decimals)}${unit}` : ''}
              </span>
              <div className="flex w-full flex-1 items-end justify-center">
                <div
                  // Square at the baseline, rounded at the data end.
                  className={`w-full max-w-6 rounded-t-[4px] ${colorFor(point.value)}`}
                  style={{ height: `${heightPct}%` }}
                  title={`${point.label}: ${point.value.toFixed(decimals)}${unit}${point.sub ? ` · ${point.sub}` : ''}`}
                />
              </div>
              {/* The axis rule: a solid hairline one step off the surface. */}
              <div className="h-px w-full shrink-0 bg-divider" />
              <span className="shrink-0 text-[10px] text-text-faint">{point.label}</span>
            </div>
          );
        })}
      </div>

      {!domain.baselineIsZero && (
        // Without this the chart overstates every difference it shows.
        <p className="mt-1 text-[10px] text-text-faint">
          {`${domain.lo}${unit}`}
        </p>
      )}

      {/* The table-view twin: every value readable without colour or hover. */}
      <ul className="sr-only">
        {points.map((p) => (
          <li key={p.label}>
            {p.label}: {p.value.toFixed(decimals)}
            {unit}
            {p.sub ? ` (${p.sub})` : ''}
          </li>
        ))}
      </ul>
    </>
  );
}

function TrendIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 17l6-6 4 4 8-8M14 7h7v7" />
    </svg>
  );
}
